import React from 'react';
import { Skeleton, Box, Stack, Card, CardContent, useTheme } from '@mui/material';

interface SkeletonCardProps {
  children?: React.ReactNode;
  height?: string | number;
  sx?: any;
  contentSx?: any;
}

const CopierLogsSkeleton = () => {
  const theme = useTheme();

  const SkeletonCard = ({ children, height, sx = {}, contentSx = {} }: SkeletonCardProps) => (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 4,
        border: `1px solid ${theme.palette.divider}`,
        ...sx
      }}
    >
      <CardContent
        sx={{
          height: '100%',
          p: { xs: 1.5, md: 2 },
          '&:last-child': { pb: { xs: 1.5, md: 2 } },
          ...contentSx
        }}
      >
        {children ? children : <Skeleton variant="rectangular" height={height || '100%'} sx={{ borderRadius: 2 }} />}
      </CardContent>
    </Card>
  );

  return (
    <Stack spacing={2}>
      <SkeletonCard>
        <Stack spacing={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width={160} height={28} />
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={{ xs: 2, md: 3 }} ml={{ xs: 0.5, md: 1.5 }}>
            <Skeleton variant="text" width={160} height={60} />
            <Skeleton variant="rectangular" width={200} height={40} sx={{ borderRadius: 2 }} />
          </Box>
          <Box display="flex" justifyContent="space-between" px={1} mt={0.5}>
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} variant="text" width={50 + Math.random() * 40} height={16} />
            ))}
          </Box>

          <Stack spacing={1}>
            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                p: 1.5
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Box display="flex" gap={1.5} alignItems="center" width="100%">
                  <Skeleton variant="circular" width={20} height={20} />
                  <Skeleton variant="rectangular" width={50} height={24} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="text" width="30%" height={24} />
                </Box>
                <Skeleton variant="text" width="10%" height={24} />
              </Box>

              <Box
                sx={{
                  bgcolor: theme.palette.action.hover,
                  borderRadius: 2,
                  p: 1.5
                }}
              >
                <Stack spacing={0.5}>
                  {[...Array(8)].map((_, i) => (
                    <Box key={i} display="flex" gap={2}>
                      <Skeleton variant="text" width={110} height={16} />
                      <Skeleton variant="text" width={50} height={16} />
                      <Skeleton variant="text" width="40%" height={16} />
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Box>

            {[...Array(3)].map((_, index) => (
              <Box
                key={index}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box display="flex" gap={1.5} alignItems="center" width="70%">
                  <Skeleton variant="circular" width={20} height={20} />
                  <Skeleton variant="rectangular" width={50} height={24} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="text" width="40%" height={24} />
                  <Skeleton variant="text" width="20%" height={24} />
                </Box>
                <Skeleton variant="text" width={50} height={24} />
              </Box>
            ))}
          </Stack>
        </Stack>
      </SkeletonCard>
    </Stack>
  );
};

export default CopierLogsSkeleton;
